import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getQrCodeContactPhoto, updateQrCodeContact } from '../api/qrCodes';
import type { QrCode, VCardContact, VCardProfile } from '../api/types';
import { getMyVCardProfile, getVCardProfileFor } from '../api/vcard';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { contactForForm, entraDifferences, withProfile } from '../contactCard';
import { formatTimestamp } from '../format';
import { ErrorBanner } from './ErrorBanner';
import { useToast } from './Toast';
import { VCardForm } from './VCardForm';

const SHOWN: ReadonlyArray<Exclude<keyof VCardContact, 'hiddenFields' | 'includePhoto'>> = [
  'title',
  'organization',
  'email',
  'mobile',
  'workPhone',
  'website',
  'linkedin',
  'street',
  'city',
  'stateRegion',
  'postcode',
  'country',
  'note',
];

function forSubmission(contact: VCardContact): VCardContact {
  const filled = Object.entries(contact)
    .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value] as const)
    .filter(([, value]) => value !== '' && value !== null && value !== undefined);

  return { ...Object.fromEntries(filled), firstName: contact.firstName.trim(), lastName: contact.lastName.trim() } as VCardContact;
}

/** The card a contact code serves. Saving it changes what every printed copy hands out. */
export function ContactCardPanel({ qrCode, onSaved }: { qrCode: QrCode; onSaved: (updated: QrCode) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [draft, setDraft] = useState<VCardContact | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncWarning, setSyncWarning] = useState<string | null>(null);
  const [profile, setProfile] = useState<VCardProfile | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { user } = useCurrentUser();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const contact = qrCode.contact!;
  const entraUserId = qrCode.contactEntraUserId;
  const hidden = contact.hiddenFields ?? [];

  // Fetched as a blob because the endpoint needs the bearer token; re-read after a save, which may bring a new one.
  useEffect(() => {
    if (!qrCode.contactHasPhoto) {
      setPhotoUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    getQrCodeContactPhoto(qrCode.id)
      .then((blob) => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setPhotoUrl(objectUrl);
        }
      })
      .catch(() => setPhotoUrl(null));

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [qrCode.id, qrCode.contactHasPhoto, qrCode.updatedAt]);

  async function syncWithEntra() {
    if (!draft || !entraUserId) {
      return;
    }

    setSyncing(true);
    try {
      const loaded = entraUserId === user?.userId ? await getMyVCardProfile() : await getVCardProfileFor(entraUserId);
      setDraft((current) => (current ? withProfile(current, loaded) : current));
      setProfile(loaded);
      setSyncWarning(loaded.warning);
      setError(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setSyncing(false);
    }
  }

  async function save() {
    if (!draft) {
      return;
    }

    setSaving(true);
    try {
      // Dated as synced only when Entra was read in this edit and the card still matches it.
      const inSync = profile !== null && Object.keys(entraDifferences(draft, profile)).length === 0;
      onSaved(await updateQrCodeContact(qrCode.id, forSubmission(draft), inSync));
      setDraft(null);
      setProfile(null);
      setSyncWarning(null);
      setError(null);
      toast.success(t('toast.contactSaved'));
    } catch (caught) {
      setError(caught);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h2>
        <i className="pi pi-id-card me-2" aria-hidden="true" />
        {t('qrCode.contact.label')}
      </h2>
      <p className="hint">{t('qrCode.contact.hint')}</p>

      <ErrorBanner error={error} />

      {draft && syncWarning && (
        <div className="alert alert-info d-flex gap-2" role="status">
          <i className="pi pi-info-circle mt-1" aria-hidden="true" />
          <span>{syncWarning}</span>
        </div>
      )}

      {draft ? (
        <VCardForm
          values={draft}
          onChange={setDraft}
          submitting={saving || syncing}
          onSubmit={save}
          hideable
          photoChoice={entraUserId !== null}
          entraValues={entraDifferences(draft, profile)}
          onRefill={entraUserId ? () => void syncWithEntra() : undefined}
          refillLabel={syncing ? t('qrCode.contact.syncing') : t('qrCode.contact.sync')}
          submitLabel={t('qrCode.contact.save')}
          submittingLabel={t('qrCode.contact.saving')}
          submitIcon="pi-check"
          onCancel={() => {
            setDraft(null);
            setProfile(null);
            setSyncWarning(null);
            setError(null);
          }}
        />
      ) : (
        <>
          {photoUrl && <img className="contact-photo" src={photoUrl} alt={t('qrCode.contact.photoAlt')} />}
          <dl className="meta">
            <dt>{t('vcard.fields.firstName')} / {t('vcard.fields.lastName')}</dt>
            <dd>
              {contact.firstName} {contact.lastName}
            </dd>
            {SHOWN.filter((key) => contact[key] && !hidden.includes(key)).map((key) => (
              <Fragment key={key}>
                <dt>{t(`vcard.fields.${key}`)}</dt>
                <dd>{contact[key]}</dd>
              </Fragment>
            ))}
            {hidden.length > 0 && (
              <>
                <dt>{t('qrCode.contact.offCard')}</dt>
                <dd>{hidden.map((key) => t(`vcard.fields.${key}`)).join(', ')}</dd>
              </>
            )}
            {qrCode.contactEntraSyncedAt && (
              <>
                <dt>{t('qrCode.contact.synced')}</dt>
                <dd>{formatTimestamp(qrCode.contactEntraSyncedAt)}</dd>
              </>
            )}
          </dl>
          {qrCode.canEdit && !qrCode.isArchived && (
            <div className="form-actions">
              <button type="button" className="btn btn-light" onClick={() => setDraft(contactForForm(contact))}>
                <i className="pi pi-pencil me-2" aria-hidden="true" />
                {t('qrCode.contact.edit')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
